import React from 'react';
import type { AvailabilityDay, AvailabilityWeek, AvailabilityMonth } from '../../types';
import { SlotChip } from './SlotChip';

// Day View Component
interface CalendarDayProps {
  data: AvailabilityDay;
  selectedSlot: string | null;
  onSlotClick: (slot: string) => void;
}

export const CalendarDay: React.FC<CalendarDayProps> = ({ data, selectedSlot, onSlotClick }) => {
  const timezone = data.expert?.timezone || 'UTC';

  if (data.slots.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-gray-500 text-lg">No available slots for this day</p>
        <p className="text-gray-400 text-sm mt-2">Try another day or switch to Week/Month view</p>
      </div>
    );
  }

  return (
    <div role="grid" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
      {data.slots.map((slot) => (
        <div key={slot} role="gridcell">
          <SlotChip
            time={slot}
            isSelected={slot === selectedSlot}
            onClick={() => onSlotClick(slot)}
            timezone={timezone}
          />
        </div>
      ))}
    </div>
  );
};

// Week View Component
interface CalendarWeekProps {
  data: AvailabilityWeek;
  selectedSlot: string | null;
  onSlotClick: (slot: string) => void;
}

export const CalendarWeek: React.FC<CalendarWeekProps> = ({ data, selectedSlot, onSlotClick }) => {
  const timezone = data.expert?.timezone || 'UTC';
  const weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  return (
    <div role="grid">
      {/* Header */}
      <div className="grid grid-cols-7 gap-2 mb-3">
        {data.days.map((day, index) => {
          const date = new Date(day.date);
          return (
            <div key={day.date} role="columnheader" className="text-center">
              <div className="text-sm font-semibold text-gray-600">{weekdays[index]}</div>
              <div className="text-lg font-bold text-gray-900">{date.getDate()}</div>
            </div>
          );
        })}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-2">
        {data.days.map((day) => (
          <div key={day.date} role="gridcell" className="border rounded-lg p-2 min-h-[120px] bg-gray-50">
            <div className="space-y-1">
              {day.slots.length === 0 ? (
                <p className="text-xs text-gray-400 text-center mt-2">No slots</p>
              ) : (
                <>
                  {day.slots.slice(0, 4).map((slot) => (
                    <SlotChip
                      key={slot}
                      time={slot}
                      isSelected={slot === selectedSlot}
                      onClick={() => onSlotClick(slot)}
                      timezone={timezone}
                    />
                  ))}
                  {day.slots.length > 4 && (
                    <p className="text-xs text-gray-500 text-center mt-1">
                      +{day.slots.length - 4} more
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Month View Component
interface CalendarMonthProps {
  data: AvailabilityMonth;
  onDateClick: (date: string) => void;
}

export const CalendarMonth: React.FC<CalendarMonthProps> = ({ data, onDateClick }) => {
  const weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  // Calculate padding for first week
  const firstDayOfMonth = new Date(data.days[0].date);
  let firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, ...
  // Convert to Monday-based (0 = Monday, 6 = Sunday)
  firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const emptyDays = Array(firstDayOfWeek).fill(null);
  const hasAnySlots = data.days.some(day => day.has_slots);

  if (!hasAnySlots) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-gray-500 text-lg">No availability this month</p>
        <p className="text-gray-400 text-sm mt-2">Try navigating to another month</p>
      </div>
    );
  }

  return (
    <div role="grid">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekdays.map((day) => (
          <div key={day} role="columnheader" className="text-center text-sm font-semibold text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {/* Empty cells for padding */}
        {emptyDays.map((_, index) => (
          <div key={`empty-${index}`} role="gridcell" className="aspect-square" />
        ))}

        {/* Date cells */}
        {data.days.map((day) => {
          const date = new Date(day.date);
          return (
            <button
              key={day.date}
              role="gridcell"
              onClick={() => day.has_slots && onDateClick(day.date)}
              disabled={!day.has_slots}
              className={`aspect-square rounded-lg flex items-center justify-center text-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                day.has_slots
                  ? 'bg-green-100 text-green-900 hover:bg-green-200 cursor-pointer'
                  : 'bg-gray-50 text-gray-400 cursor-not-allowed'
              }`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};
